import { format } from "date-fns";
import { getUploadFileSizeError } from "@asap/shared/constants/file";
import type { BaseValidationField } from "./handlers/types";

/**
 * Default accepted MIME types for document uploads
 * Images (JPEG, PNG, WebP) and PDF files
 */
export const DEFAULT_ACCEPTED_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;

/**
 * Converts domain data to form values suitable for HTML inputs
 * - Date strings (ISO format) → formatted for date inputs (YYYY-MM-DD)
 * - Date objects → ISO date strings (YYYY-MM-DD) for date inputs
 * - Other values passed through unchanged
 *
 * @param data Domain data (from OCR, database, etc.)
 * @param fields Field definitions with type information
 * @returns Form values ready for v-model binding
 */
export function toFormValues(
  data: Record<string, unknown>,
  fields: readonly BaseValidationField[]
): Record<string, string> {
  const formValues: Record<string, string> = {};

  for (const [key, value] of Object.entries(data)) {
    const field = fields.find((f) => String(f.key) === key);

    // Convert dates to string format for date inputs
    if (field?.type === "date" && value) {
      const date = value instanceof Date ? value : new Date(String(value));
      // Only format if valid date, otherwise convert to string
      if (!isNaN(date.getTime())) {
        formValues[key] = format(date, "yyyy-MM-dd");
      } else {
        formValues[key] = String(value);
      }
    } else {
      // Convert all values to strings for form binding
      formValues[key] = value != null ? String(value) : "";
    }
  }

  return formValues;
}

/**
 * Converts form string values back to domain-appropriate types using field definitions.
 * Reverse of `toFormValues()`.
 * - Radio/Checkbox fields with "true"/"false" values → boolean
 * - Radio/Checkbox fields with other values (e.g., empty string) → undefined
 * - All other values passed through as-is
 *
 * @param data Form values (all strings)
 * @param fields Field definitions with type information
 * @returns Domain data with proper types
 */
export function fromFormValues(
  data: Record<string, string>,
  fields: readonly BaseValidationField[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    const field = fields.find((f) => String(f.key) === key);

    if (field?.type === "radio" || field?.type === "checkbox") {
      if (value === "true" || value === "false") {
        result[key] = value === "true";
      } else {
        result[key] = undefined;
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Validates files against accepted MIME types
 * Filters valid and invalid files for user feedback
 *
 * @param files Files to validate
 * @param acceptedMimeTypes Allowed MIME types
 * @returns Object with valid and invalid file arrays
 *
 * @example
 * ```typescript
 * const { valid, invalid } = validateFileTypes(files, config.acceptedMimeTypes);
 * if (invalid.length > 0) {
 *   showWarning(`Fichiers non autorisés: ${invalid.map(f => f.name).join(', ')}`);
 * }
 * ```
 */
export function validateFileTypes(
  files: File[],
  acceptedMimeTypes: string[]
): {
  valid: File[];
  invalid: File[];
} {
  const valid: File[] = [];
  const invalid: File[] = [];

  files.forEach((file) => {
    if (acceptedMimeTypes.includes(file.type)) {
      valid.push(file);
    } else {
      invalid.push(file);
    }
  });

  return { valid, invalid };
}

/**
 * Splits files on the size the API accepts, so a file it would refuse is caught
 * before the user fills the verification form.
 */
export function validateFileSizes(files: File[]): {
  valid: File[];
  rejectionMessages: string[];
} {
  const valid: File[] = [];
  const rejectionMessages: string[] = [];

  files.forEach((file) => {
    const fileSizeError = getUploadFileSizeError(file.size);
    if (fileSizeError) {
      rejectionMessages.push(`${file.name} : ${fileSizeError}`);
    } else {
      valid.push(file);
    }
  });

  return { valid, rejectionMessages };
}
